import { type FC, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import type { Merchant } from '../models/types';
import type { OverridableStringUnion } from '@mui/types';

import { DataContext } from '../contexts/DataContext';
import {
  createBackendUser,
  createBrandTransaction,
  createMerchant,
} from '../fetchFns';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import useDialog from '../hooks/useDialog';

const transactionTypes = ['credit', 'debit'] as const;

type FormValues = {
  merchant_id: string;
  transaction_type: 'credit' | 'debit' | undefined;
  amount: number;
  reason_id: string;
  remarks: string;
};

const defaultValues: FormValues = {
  merchant_id: '',
  transaction_type: 'credit',
  amount: 0,
  reason_id: '',
  remarks: '',
};

export default function BrandTransactionDialog({
  getData,
}: {
  getData: () => Promise<void>;
}) {
  const {
    handleSubmit,
    control,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });
  const dataCtx = useContext(DataContext);
  const backdrop = useBackdrop();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const [brands, setBrands] = useState<Merchant[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (data: FormValues) => {
    const formData = new FormData();
    backdrop.handleBackdropOpen();

    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value?.toString() ?? '');
    }

    let message = `The transaction has been created.`;
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const response = await createBrandTransaction(formData);
      reset();
      await getData();
      dialog.handleDialogClose();
    } catch (error) {
      message = String(error);
      severity = 'error';
    }
    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  const getMerchants = async () => {
    backdrop.handleBackdropOpen();
    try {
      const { data, error } = await supabase
        .schema('app')
        .from('merchants')
        .select('*');

      if (error) {
        console.error('Error fetching brands:', error);
        throw error;
      }

      setBrands(data);
    } catch (error) {
      const errorCast = error as Error;

      console.error(errorCast.stack);
      console.error(errorCast.name);
      snackbar.handleSnackbarOpen(String(error), 'error');
    }
    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getMerchants();
  }, []);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        <Controller
          name="merchant_id"
          control={control}
          rules={{ required: 'Brand is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Brand"
              error={!!errors.merchant_id}
              helperText={errors.merchant_id?.message}
              required
              fullWidth>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="transaction_type"
          control={control}
          rules={{ required: 'Transaction type is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              disabled
              label="Transaction Type"
              error={!!errors.transaction_type}
              helperText={errors.transaction_type?.message}
              required
              fullWidth>
              {transactionTypes.map((transactionType) => (
                <MenuItem key={transactionType} value={transactionType}>
                  {transactionType}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="amount"
          control={control}
          rules={{ required: 'Amount is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Amount"
              error={!!errors.amount}
              helperText={errors.amount?.message}
              required
              fullWidth
            />
          )}
        />

        <Controller
          name="reason_id"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="text"
              label="Reason ID"
              error={!!errors.reason_id}
              helperText={errors.reason_id?.message}
              fullWidth
            />
          )}
        />
        <Controller
          name="remarks"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Remarks"
              error={!!errors.remarks}
              helperText={errors.remarks?.message}
              fullWidth
            />
          )}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}>
          Create Transaction
        </Button>
      </Stack>
    </Box>
  );
}
